<script setup>
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import Input from 'dashboard/components-next/input/Input.vue';
import SettingsLayout from '../SettingsLayout.vue';
import { computed, ref } from 'vue';
import { picoSearch } from '@chatwoot/pico-search';
import { useMapGetter } from 'dashboard/composables/store.js';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import EmojiIcon from 'dashboard/components-next/emoji-icon-picker/EmojiIcon.vue';

const store = useStore();
const { t } = useI18n();
const getters = useStoreGetters();
const { isAdmin } = useAdmin();

const loading = ref({});
const searchQuery = ref('');

const teamsList = useMapGetter('teams/getTeams');

const filteredTeamsList = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return teamsList.value;
  return picoSearch(teamsList.value, query, ['name', 'description']);
});

const uiFlags = computed(() => getters['teams/getUIFlags'].value);

const deleteTeam = async ({ id }) => {
  try {
    loading.value[id] = true;
    await store.dispatch('teams/delete', id);
    useAlert(t('TEAMS_SETTINGS.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('TEAMS_SETTINGS.DELETE.API.ERROR_MESSAGE'));
  } finally {
    loading.value[id] = false;
  }
};

const showDeletePopup = ref(false);
const selectedTeam = ref({});

const openDelete = team => {
  showDeletePopup.value = true;
  selectedTeam.value = team;
};

const closeDelete = () => {
  showDeletePopup.value = false;
  selectedTeam.value = {};
};

const confirmDeletion = () => {
  deleteTeam(selectedTeam.value);
  closeDelete();
};

const deleteConfirmText = computed(
  () => `${t('TEAMS_SETTINGS.DELETE.CONFIRM.YES')} ${selectedTeam.value.name}`
);

const deleteRejectText = computed(() => t('TEAMS_SETTINGS.DELETE.CONFIRM.NO'));

const confirmDeleteTitle = computed(() =>
  t('TEAMS_SETTINGS.DELETE.CONFIRM.TITLE', {
    teamName: selectedTeam.value.name,
  })
);

const confirmPlaceHolderText = computed(() =>
  t('TEAMS_SETTINGS.DELETE.CONFIRM.PLACE_HOLDER', {
    teamName: selectedTeam.value.name,
  })
);
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('TEAMS_SETTINGS.LOADING')"
    :no-records-found="!teamsList.length"
    :no-records-message="$t('TEAMS_SETTINGS.LIST.404')"
  >
    <template #header>
      <div class="rounded-2xl border border-n-weak bg-n-solid-2 p-5 sm:p-6">
        <div class="flex flex-wrap items-start justify-between gap-4">
          <div class="flex items-start gap-3">
            <span
              class="grid size-11 shrink-0 place-items-center rounded-xl bg-n-alpha-2"
              ><Icon
                icon="i-hugeicons-user-group"
                class="size-6 text-n-slate-11"
            /></span>
            <div>
              <h1 class="mb-1 text-xl font-semibold text-n-slate-12">
                {{ $t('TEAMS_SETTINGS.HEADER') }}
              </h1>
              <p class="mb-0 max-w-xl text-sm leading-relaxed text-n-slate-11">
                {{ $t('TEAMS_SETTINGS.REDESIGN.INTRO') }}
              </p>
            </div>
          </div>
          <router-link v-if="isAdmin" :to="{ name: 'settings_teams_new' }">
            <Button
              icon="i-hugeicons-add-01"
              :label="$t('TEAMS_SETTINGS.NEW_TEAM')"
            />
          </router-link>
        </div>
        <div
          class="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-n-weak pt-4"
        >
          <Input
            v-model="searchQuery"
            class="w-full sm:max-w-sm"
            :placeholder="$t('TEAMS_SETTINGS.SEARCH_PLACEHOLDER')"
            :aria-label="$t('TEAMS_SETTINGS.SEARCH_PLACEHOLDER')"
          />
          <span
            class="rounded-md bg-n-alpha-2 px-2.5 py-1 text-xs text-n-slate-11"
            >{{ $t('TEAMS_SETTINGS.COUNT', { n: teamsList.length }) }}</span
          >
        </div>
      </div>
    </template>
    <template #body>
      <span
        v-if="!filteredTeamsList.length && searchQuery"
        class="flex-1 flex items-center justify-center py-20 text-center text-body-main !text-base text-n-slate-11"
      >
        {{ $t('TEAMS_SETTINGS.NO_RESULTS') }}
      </span>

      <div v-else class="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <div
          v-for="team in filteredTeamsList"
          :key="team.id"
          class="group relative flex min-w-0 flex-col justify-between gap-3 rounded-xl border border-n-weak bg-n-solid-2 p-4 transition-colors hover:border-n-strong"
        >
          <router-link
            v-if="isAdmin"
            :to="{ name: 'settings_teams_edit', params: { teamId: team.id } }"
            :aria-label="`${$t('TEAMS_SETTINGS.LIST.EDIT_TEAM')}: ${team.name}`"
            class="absolute inset-0 z-10 rounded-xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
          />
          <div class="flex min-w-0 items-start gap-3">
            <div
              class="flex items-center flex-shrink-0 size-10 justify-center rounded-xl outline outline-1 outline-n-weak -outline-offset-1 text-lg"
            >
              <EmojiIcon
                v-if="team.icon"
                :value="team.icon"
                :color="team.icon_color"
                class="size-5"
              />
              <Icon
                v-else
                icon="i-lucide-users-round"
                class="size-4 text-n-slate-11"
              />
            </div>
            <div class="flex min-w-0 flex-col items-start gap-1">
              <span
                class="line-clamp-2 break-words text-sm font-semibold text-n-slate-12"
              >
                {{ team.name }}
              </span>
              <p
                class="mb-0 line-clamp-2 break-words text-xs leading-relaxed text-n-slate-11"
              >
                {{
                  team.description ||
                  $t('TEAMS_SETTINGS.REDESIGN.NO_DESCRIPTION')
                }}
              </p>
            </div>
          </div>
          <div
            class="flex items-center justify-between gap-2 border-t border-n-weak pt-3"
          >
            <span class="text-xs text-n-slate-11">{{
              $t(
                team.allow_auto_assign
                  ? 'TEAMS_SETTINGS.REDESIGN.AUTO_ON'
                  : 'TEAMS_SETTINGS.REDESIGN.AUTO_OFF'
              )
            }}</span>
            <div class="flex shrink-0 items-center gap-1">
              <span
                v-if="isAdmin"
                class="i-hugeicons-arrow-right-02 size-4 text-n-slate-11"
                aria-hidden="true"
              />

              <Button
                v-if="isAdmin"
                v-tooltip.top="$t('TEAMS_SETTINGS.DELETE.BUTTON_TEXT')"
                icon="i-hugeicons-delete-02"
                color="slate"
                variant="ghost"
                size="sm"
                class="relative z-20 hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                :is-loading="loading[team.id]"
                @click.stop="openDelete(team)"
              />
            </div>
          </div>
        </div>
      </div>
    </template>
    <woot-confirm-delete-modal
      v-if="showDeletePopup"
      v-model:show="showDeletePopup"
      :title="confirmDeleteTitle"
      :message="$t('TEAMS_SETTINGS.DELETE.CONFIRM.MESSAGE')"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
      :confirm-value="selectedTeam.name"
      :confirm-place-holder-text="confirmPlaceHolderText"
      @on-confirm="confirmDeletion"
      @on-close="closeDelete"
    />
  </SettingsLayout>
</template>
